/**
 * WEB-1000 — the named utilities registered by the `@theme inline reference`
 * block in `styles/theme.css` (and the `duration-*` `@utility` rules in
 * `styles/index.css`), mirrored for tailwind-merge.
 *
 * `cn()` runs every class list through tailwind-merge, which only knows a
 * utility belongs to a conflict group when the value is on its scale. Without
 * this list `text-small` reads as a text COLOUR (so `cn("text-small",
 * "text-fg-2")` drops the size), `rounded-pill` / `z-modal` join no group,
 * and the `loci-*` shadow names read as shadow colours.
 *
 * Kept in lockstep with styles/theme.css by
 * `styles/__tests__/theme-token-utilities.test.ts` — add a token there and the
 * test names the entry missing here.
 */
export const THEME_UTILITY_NAMES = {
  color: [
    'state-live',
    'state-live-ink',
    'bg',
    'bg-elev',
    'ds-surface',
    'ds-surface-alt',
    'fg-1',
    'fg-2',
    'fg-3',
    'fg-inverse',
    'ds-border',
    'border-strong',
    'ds-ring',
    'ds-accent',
    'ds-accent-hover',
    'accent-fg',
    'accent-text',
    'resale-ink',
    'resale-border',
    'resale-wash',
    'cta',
    'cta-fg',
    'success',
    'danger',
    'warning',
    'n-950',
    'n-900',
    'n-0',
    'accent-wash',
    'cta-hover',
    'clt-skyline-border',
    'clt-navy',
    'clt-ember-wash',
    'clt-ember-border',
    'clt-ember-fg-on-wash',
    'clt-skyline-glow-fg',
    'clt-ember-fg',
    'clt-skyline-fg',
    'clt-ember-deep',
    'clt-cta-ink',
    'admin-accent',
    'admin-accent-hover',
    'admin-accent-deep',
    'admin-accent-wash',
    'admin-accent-line',
    'admin-amber',
    'admin-amber-wash',
    'admin-info',
    'admin-info-wash',
    'cat-music',
    'cat-sports',
    'cat-food',
    'cat-science-tech',
    'cat-art',
    'cat-drinks',
    'cat-business-networking',
    'cat-community-culture',
    'cat-wellness',
    'cat-hobbies-interests',
    'hood-uptown',
    'hood-south-end',
    'hood-plaza-midwood',
    'hood-noda',
    'hood-elizabeth',
    'hood-dilworth',
    'hood-south-park',
    'hood-loso',
    'hood-freemorewest',
    'hood-camp-north-end',
    'hood-myers-park',
    'hood-historic-west-end',
    'hood-optimist-park',
    'hood-cotswold',
    'hood-wesley-heights',
    'hood-wilmore',
    'hood-cherry',
    'hood-sedgefield',
    'hood-belmont',
    'nav-strip-bg',
    'nav-bar-bg',
    'nav-border',
    'nav-strip-border',
    'nav-fg',
    'nav-fg-muted',
    'nav-accent',
    'nav-cta',
    'nav-unread',
    'nav-ghost-bg',
    'nav-accent-border',
    'nav-lettermark',
    'nav-sheet-bg',
    'nav-dock-bg',
    'nav-sheet-overlay',
    'footer-coord-fg',
    'gn-accent',
    'gn-accent-glow',
    'gn-accent-wash',
    'evt-chip-bg',
    'evt-chip-border',
    'evt-map-a',
    'evt-map-b',
    'evt-map-grid',
    'evt-map-road',
    'evt-map-dot',
    'evt-map-ink',
    'switch-knob-on',
    'ev-image-surface-base-top',
    'ev-image-surface-base-bottom',
    'lm-sk-base',
    'lm-sk-sheen',
    'lm-sk-sheen-soft',
    'ds-brand-primary',
    'ds-brand-primary-light',
    'ds-brand-primary-dark',
    'ds-brand-accent',
    'ds-glass-bg',
    'ds-glass-bg-strong',
    'ds-glass-border',
    'ds-glass-border-strong',
    'loci-amber',
    'loci-amber-light',
    'loci-amber-dark',
    'loci-indigo',
    'loci-cream',
    'loci-primary',
    'loci-primary-light',
    'loci-primary-dark',
    'loci-primary-foreground',
    'loci-secondary',
    'loci-secondary-foreground',
    'loci-success',
    'loci-warning',
    'loci-error',
    'loci-info',
    'loci-background',
    'loci-surface',
    'loci-surface-alt',
    'loci-text',
    'loci-text-muted',
    'loci-text-secondary',
    'loci-border',
    'loci-ring',
    'loci-category-music-primary',
    'loci-category-music-light',
    'loci-category-music-dark',
    'loci-category-sports-primary',
    'loci-category-sports-light',
    'loci-category-sports-dark',
    'loci-category-food-primary',
    'loci-category-food-light',
    'loci-category-food-dark',
    'loci-category-tech-primary',
    'loci-category-tech-light',
    'loci-category-tech-dark',
    'loci-category-art-primary',
    'loci-category-art-light',
    'loci-category-art-dark',
    'loci-category-drinks-primary',
    'loci-category-drinks-light',
    'loci-category-drinks-dark',
    'loci-neutral-50',
    'loci-neutral-100',
    'loci-neutral-200',
    'loci-neutral-300',
    'loci-neutral-400',
    'loci-neutral-500',
    'loci-neutral-600',
    'loci-neutral-700',
    'loci-neutral-800',
    'loci-neutral-900',
    'loci-bg-elev',
    'loci-surface-card',
    'loci-surface-card-alt',
    'loci-border-strong',
    'loci-text-inverse',
    'loci-text-strong',
    'loci-text-quiet',
    'loci-ring-focus',
  ],
  font: ['display', 'body', 'mono'],
  text: [
    'micro',
    'caption',
    'small',
    'body',
    'lead',
    'h6',
    'h5',
    'h4',
    'h3',
    'h2',
    'h1',
    'display',
    'mega',
  ],
  radius: [
    'loci-none',
    'loci-xs',
    'loci-sm',
    'loci-md',
    'loci-lg',
    'loci-xl',
    'loci-2xl',
    'loci-pill',
    'loci-full',
    'loci-card-lg',
    'loci-card-xl',
    'loci-card-2xl',
    'ds-none',
    'ds-xs',
    'ds-sm',
    'ds-md',
    'ds-lg',
    'ds-xl',
    'ds-2xl',
    'pill',
  ],
  spacing: [
    'gutter',
    'flow',
    'flow-section',
    'flow-head',
    'card-pad',
    'footer-pad-top',
    'chrome-height',
    'toast-inset-top',
    'toast-inset-bottom',
    'toast-inset-left',
    'toast-stack-width',
    'consent-bar-h',
    'nav-dock-h',
    'nav-stick-top',
  ],
  container: ['page'],
  shadow: [
    'xs',
    'sm',
    'md',
    'lg',
    'xl',
    '2xl',
    'loci-sm',
    'loci-md',
    'loci-lg',
    'loci-xl',
    'loci-2xl',
    'nav-elevation',
  ],
  ease: ['expressive-out', 'state'],
  tracking: ['eyebrow', 'caps', 'mega', 'display', 'h1', 'h2', 'h3', 'body'],
  leading: ['ds-tight', 'ds-snug', 'ds-normal', 'ds-loose'],
  zIndex: [
    'surface-sticky',
    'chrome',
    'scrim',
    'sheet',
    'overlay',
    'dock',
    'fab',
    'modal',
    'modal-nested',
    'toast',
  ],
  backgroundImage: ['ev-image-surface', 'grad-skyline'],
  blur: ['nav-sheet', 'nav-backdrop', 'nav-dock'],
  duration: ['micro', 'press', 'standard', 'hero', 'ambient', 'strip-crossfade'],
} as const satisfies Record<string, readonly string[]>;

export type ThemeUtilityNamespace = keyof typeof THEME_UTILITY_NAMES;
